/** Missing shared-client capabilities kept local until approved for b2b-common. */
import type { SeatMap } from "../features/tours/types";
import type { AxiosInstance } from "axios";
import { DocumentsClient } from "b2b-common/services/document";
import type {
  BookingClient,
  BookingPax,
} from "b2b-common/models/accommodation";
export interface BusPoint {
  id: number;
  name: string;
}
export interface BusDate {
  date: string;
  available: number;
}
export interface BusSearch {
  from: number;
  to: number;
  route_type: "out" | "in" | "return";
  date_from: string;
  date_to: string | null;
  adults: number;
  child_ages: number[];
}
export interface BusResult {
  index: number;
  label: string;
  from_name: string;
  to_name: string;
  bus_name: string;
  description: string;
  image: string;
  out_departure_date: string;
  out_departure_time: string;
  out_arrival_date: string;
  out_arrival_time: string;
  in_departure_date: string;
  in_departure_time: string;
  price: number;
  tax: number;
  commission: number;
  dep_pick_up_details: string;
  dest_drop_off_details: string;
}
export interface BusBooking {
  result: number;
  pax: BookingPax[];
  tel: string;
  dropOff: string;
  notes: string;
  client?: BookingClient;
}
export class BusClient {
  constructor(private readonly http: AxiosInstance) {}
  async destinations(): Promise<BusPoint[]> {
    return (
      await this.http.get("authOp.php", {
        params: { action: "getBusDestinationPoints" },
      })
    ).data;
  }
  async departures(destination: number): Promise<BusPoint[]> {
    return (
      await this.http.get("authOp.php", {
        params: { action: "getBusDeparturePoints", destination },
      })
    ).data;
  }
  async dates(
    from: number,
    to: number,
    type: "out" | "in",
  ): Promise<BusDate[]> {
    return (
      await this.http.get("authOp.php", {
        params: { action: "getBusDates", from, to, type },
      })
    ).data;
  }
  async seatMap(
    departure_group: number,
    date: string,
    time: string,
  ): Promise<SeatMap> {
    const { data } = await this.http.get("authOp.php", {
      params: { action: "getBusSeatMapInfo", departure_group, date, time },
    });
    if (!Array.isArray(data?.seat_map?.data?.decks))
      throw new Error("Invalid seat map");
    return data;
  }
  async initialize(): Promise<void> {
    await this.http.get("authOp.php", {
      params: { action: "getBusSearchParams" },
    });
  }
  async search(params: BusSearch): Promise<BusResult[]> {
    const { data } = await this.http.post("authOp.php", params, {
      params: { action: "runBusSearch" },
    });
    if (!Array.isArray(data)) throw new Error("Invalid coach results");
    return data;
  }
  async book(params: BusBooking): Promise<number> {
    const { data } = await this.http.post("authOp.php", params, {
      params: { action: "bookBus" },
    });
    const id = Number(Array.isArray(data) ? data[0] : data);
    if (!Number.isInteger(id) || id <= 0)
      throw new Error("Unconfirmed coach booking");
    return id;
  }
}
export class OfferDocumentsClient extends DocumentsClient {
  constructor(private readonly http: AxiosInstance) {
    super(http);
  }
  async downloadOffer(
    kind: "package" | "hotel" | "tour",
    selection: string,
    fileName: string,
  ): Promise<void> {
    const { data } = await this.http.get("authOp.php", {
      params: {
        // Both wrappers render an opaque payload; PrintEntry dispatches tour documents.
        action: kind === "hotel" ? "printHotelOffer" : "printPackageOffer",
        data: selection,
      },
    });
    if (
      data?.mimeType !== "application/pdf" ||
      typeof data.content !== "string" ||
      !data.content
    )
      throw new Error("Invalid PDF response");
    this.downloadPdf({ pdfData: data.content, fileName });
  }
}
